import { Box, Hidden, makeStyles, useTheme } from "@material-ui/core";
import {
  DsLinearProgress,
  DsProgressTracker,
  DsText,
  IconProps,
  ListItemInfo,
  size,
} from "@mvp/design-system";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDependentStore, useSidebarStore } from "../../../hooks";
import routes from "../../../routes";
import { useTranslation } from "react-i18next";

const enum TaskSteps {
  ELIGIBILITY = "eligibility",
  SUBSCRIBER_INFO = "subscriberInfo",
  DEPENDENT_INFO = "dependentInfo",
  REVIEW_SELECTION = "reviewSelection",
}

const useStyles = makeStyles((theme) => ({
  sidebar: {
    background: theme.palette.grey[50],
    marginRight: 0,
    width: "21.875rem",
    minWidth: "21.875rem",
    padding: `${size.m(theme)} ${size.l(theme)} ${size.l(theme)}`,
    [theme.breakpoints.down("md")]: {
      width: "20rem",
      minWidth: "20rem",
    },
  },
  mobileText: {
    margin: `${size.xs(theme)} 0 ${size.xxxs(theme)} ${size.s(theme)}`,
  },
  mobileProgress: {
    width: "100%",
  },
  iconCompletedOrActive: {
    color: theme.palette.primary.main,
  },
  iconDisabled: {
    color: theme.palette.grey[400]
  },
  raisedProgressbar: {
    zIndex: 1099
  }
}));

const createIconInfo = (
  task: TaskSteps,
  isComplete: boolean,
  locationPath: string,
  activeStyling: string
): IconProps => {
  const isActiveOrComplete =
    isCurrentLocation(locationPath, task) || isComplete;
  return {
    title: `${task}-task-icon`,
    icon: getStatusIcon(isComplete),
    iconFamily: "far",
    className: isActiveOrComplete ? activeStyling : "",
  };
};
const getStatusIcon = (isComplete: boolean) => {
  return isComplete ? "check-circle" : "circle";
};

const isCurrentLocation = (locationPath: string, taskStep: TaskSteps) => {
  return locationPath.includes(routes[taskStep].base);
};

const getVariant = (locationPath: string, taskStep: TaskSteps) => {
  return isCurrentLocation(locationPath, taskStep) ? "button" : "body2";
};

const SideBar = () => {
  const classes = useStyles();
  const history = useHistory();
  const [t] = useTranslation();
  const locationPath = (useLocation()).pathname;

  const {
    sidebarStore: {
      activeStep,
      hasCompleteDependentInfo,
      hasCompleteEligibility,
      hasCompleteSubscriberInfo,
      hasReviewedSelections
    },
  } = useSidebarStore();

  const {
    dependentStore: { dependents, spouse },
  } = useDependentStore();

  const theme = useTheme();

  const enableSubscriberStep = activeStep >= 2 && hasCompleteEligibility;
  const enableDependentStep = activeStep >= 3 && hasCompleteSubscriberInfo && (!!spouse || dependents.length > 0);
  const enableReviewStep = activeStep >= 4 && hasReviewedSelections;

  const onRedirectTo = (currentTask: TaskSteps) => {
    history.push(routes[currentTask].home);
  };

  const checklistItems: ListItemInfo[] = [
    {
      itemName: `1. ${t('sidebar.step1')}`,
      itemId: "eligibility-task",
      iconInfo: createIconInfo(
        TaskSteps.ELIGIBILITY,
        hasCompleteEligibility,
        locationPath,
        classes.iconCompletedOrActive
      ),
      textVariant: getVariant(locationPath, TaskSteps.ELIGIBILITY),
      onClick: () => {
        onRedirectTo(TaskSteps.ELIGIBILITY);
      },
    },
    {
      itemName: `2. ${t('sidebar.step2')}`,
      itemId: "subscriber-task",
      iconInfo: createIconInfo(
        TaskSteps.SUBSCRIBER_INFO,
        hasCompleteSubscriberInfo,
        locationPath,
        enableSubscriberStep ? classes.iconCompletedOrActive : classes.iconDisabled
      ),
      textVariant: getVariant(locationPath, TaskSteps.SUBSCRIBER_INFO),
      textColor: enableSubscriberStep ? theme.palette.text.primary : theme.palette.text.disabled,
      onClick: () => enableSubscriberStep && onRedirectTo(TaskSteps.SUBSCRIBER_INFO),
    },
    {
      itemName: `3. ${t('sidebar.step3')}`,
      itemId: "dependent-task",
      iconInfo: createIconInfo(
        TaskSteps.DEPENDENT_INFO,
        hasCompleteDependentInfo,
        locationPath,
        enableDependentStep ? classes.iconCompletedOrActive : classes.iconDisabled
      ),
      textVariant: getVariant(locationPath, TaskSteps.DEPENDENT_INFO),
      textColor: enableDependentStep ? theme.palette.text.primary : theme.palette.text.disabled,
      onClick: () => enableDependentStep && onRedirectTo(TaskSteps.DEPENDENT_INFO),
    },
    {
      itemName: `4. ${t('sidebar.step4')}`,
      itemId: "review-task",
      iconInfo: createIconInfo(
        TaskSteps.REVIEW_SELECTION,
        hasReviewedSelections,
        locationPath,
        enableReviewStep ? classes.iconCompletedOrActive : classes.iconDisabled
      ),
      textVariant: getVariant(locationPath, TaskSteps.REVIEW_SELECTION),
      textColor: enableReviewStep ? theme.palette.text.primary : theme.palette.text.disabled,
      onClick: () => enableReviewStep && onRedirectTo(TaskSteps.REVIEW_SELECTION),
    },
  ];

  const isEnrollmentComplete = locationPath === routes.reviewSelection.thankYou;

  return (
    <>
      <Hidden mdUp>
        <Box className={classes.mobileProgress}>
          <DsLinearProgress
            value={(activeStep / checklistItems.length) * 100}
            variant="determinate"
            className={classes.raisedProgressbar}
            style={{ position: 'fixed', width: '100%' }}
          />
          <DsText
            variant="subtitle2"
            text={`Step ${activeStep} of ${checklistItems.length}`}
            className={classes.mobileText}
          />
        </Box>
      </Hidden>
      <Hidden smDown>
        <Box className={classes.sidebar}>
          <DsProgressTracker
            text={isEnrollmentComplete ? t("sidebar.complete"): t("sidebar.progress")}
            steps={["1", "2", "3", "4"]}
            activeStep={activeStep}
            checklistItems={checklistItems}
          />
        </Box>
      </Hidden>
    </>
  );
};

export default SideBar;
